import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { OfferType, PropertyTypeNumber } from '@/models/valuationFunnel';
import { Location } from '@/stores/types/propertyValuation';
import { HgSelectOptions } from '@smg-real-estate/domus/types';
import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface radioOptions {
  label: string;
  value: string | boolean;
  trackingValue?: string;
}

export interface ValuationFormModel {
  address: Location;
  rooms: string;
  offerType: string;
  propertyType: string;
  whenToSell: string;
  wantToSell: string;
  isRenovated: string;
  livingSpace: string;
  lotSize: string;
  buildYear: string;
  renovationYear: string;
  culture: string;
  selectedApartment: string;
  isConsultantRequested: string;
  phoneNumberData: {
    valid: boolean | undefined;
    number: string | null;
  };
}

interface PlaceholderOptionProps {
  disabled?: boolean;
}

export const usePlaceholderOption = ({
  disabled = true,
}: PlaceholderOptionProps = {}): Ref<HgSelectOptions> => {
  const { t } = useI18n();
  return computed(() => ({
    value: '',
    text: t('common.placeholder.select'),
    disabled,
  }));
};

export const roomOptions: HgSelectOptions[] = [
  { text: '1', value: '1' },
  { text: '1.5', value: '1.5' },
  { text: '2', value: '2' },
  { text: '2.5', value: '2.5' },
  { text: '3', value: '3' },
  { text: '3.5', value: '3.5' },
  { text: '4', value: '4' },
  { text: '4.5', value: '4.5' },
  { text: '5', value: '5' },
  { text: '5.5', value: '5.5' },
  { text: '6', value: '6' },
  { text: '7', value: '7' },
  { text: '8', value: '8' },
  { text: '8+', value: '9' },
];

export const renovateOptions: radioOptions[] = [
  { value: true, label: 'answer.yes' },
  { value: false, label: 'answer.no' },
];

/** Ownership options: Yes = Own (3), No = Other (2) - focuses solely on ownership status */
export const ownershipOptions: radioOptions[] = [
  {
    label: 'answer.yes',
    value: OfferType.Own.toString(),
  },
  {
    label: 'answer.no',
    value: OfferType.Other.toString(),
  },
];

export const propertyTypeOptions: radioOptions[] = [
  {
    value: '1',
    label: 'common.house',
  },
  {
    value: '0',
    label: 'common.apartment',
  },
];

export const sellingPeriodOptions: radioOptions[] = [
  {
    label: 'whenToSell.asap',
    value: '0',
  },
  {
    label: 'whenToSell.nextSixMonths',
    value: '1',
  },
  {
    label: 'whenToSell.nextTwoYears',
    value: '2',
  },
  {
    label: 'whenToSell.overTwoYears',
    value: '3',
  },
];

const serializeAddress = (property: PropertyValuationResult) => {
  if (property.street && property.zipCode && property.town) {
    return {
      street: property.street,
      postalCode: property.zipCode,
      city: property.town,
      lat: property.lat,
      lon: property.lon,
      formattedAddress: `${property.street}, ${property.zipCode} ${property.town}`,
    };
  }
  return undefined;
};

const serializeOfferType = (property: PropertyValuationResult) => {
  const result: Partial<ValuationFormModel> = {};

  // Map to ownership-only options: Own (3) or Selling (0) = Yes, Buying (1) or Other (2) = No
  result.offerType =
    property.offerType === OfferType.Own || property.offerType === OfferType.Selling
      ? OfferType.Own.toString()
      : OfferType.Other.toString();

  // Clear selling-intent fields - section removed from form, prevent stale bindings for
  // properties that had whenToSell/wantToSell set before the form change
  result.wantToSell = '';
  result.whenToSell = '';

  return result;
};

export const serializePropertyValuationToValuationFormModel = (
  property: PropertyValuationResult,
): Partial<ValuationFormModel> => {
  const formModel: Partial<ValuationFormModel> = {};

  const address = serializeAddress(property);
  if (address) {
    formModel.address = address;
  }

  formModel.propertyType = property.propertyType.toString();
  formModel.livingSpace = property.livingSpace.toString();
  formModel.rooms = property.room.toString();
  formModel.buildYear = property.yearBuild.toString();
  formModel.isRenovated = property.isRenovated.toString();

  if (property.propertyType === PropertyTypeNumber.House && property.lotSize) {
    formModel.lotSize = property.lotSize.toString();
  }

  if (property.isRenovated && property.renovationYear) {
    formModel.renovationYear = property.renovationYear.toString();
  }

  if (typeof property.isConsultantRequested === 'boolean') {
    formModel.isConsultantRequested = property.isConsultantRequested.toString();
  }

  Object.assign(formModel, serializeOfferType(property));

  return formModel;
};
